import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Plus, Trash2 } from 'lucide-react';
import { apiClient } from '../../api/client';
import { MoneyInput } from '../../components/MoneyInput';
import { useMe } from '../../api/hooks';
import { usePageTitle } from '../../hooks/usePageTitle';
import { useVisit } from '../visits/hooks';
import type { CreateLineItem, ReceiptDetail } from './types';

type Step = 'review' | 'extras' | 'confirm';

interface CreateFinalPayload {
  visitId: string;
  manualLineItems: CreateLineItem[];
  adjustments: number;
  includeMileage: boolean;
  notes?: string | null;
}

export function CloseVisitWizard() {
  usePageTitle('Close visit');
  const { id = '' } = useParams();
  const nav = useNavigate();
  const { data: visit } = useVisit(id);
  const { data: me } = useMe(true);

  const [step, setStep] = useState<Step>('review');
  const [includeMileage, setIncludeMileage] = useState(true);
  const [manual, setManual] = useState<CreateLineItem[]>([]);
  const [adjustments, setAdjustments] = useState<number>(0);
  const [notes, setNotes] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const perMileRate = me?.settings.perMileRate ?? 0;

  const unbilled = visit?.expenses.filter((e) => !e.billed) ?? [];
  const mileageLine =
    visit?.mileageMiles && visit.mileageMiles > 0 && includeMileage
      ? { miles: visit.mileageMiles, rate: perMileRate, total: round2(visit.mileageMiles * perMileRate) }
      : null;

  const subtotal = useMemo(() => {
    const expenseTotal = unbilled.reduce((s, e) => s + e.amount, 0);
    const manualTotal = manual.reduce((s, m) => s + m.quantity * m.unitPrice, 0);
    const mileageTotal = mileageLine?.total ?? 0;
    return round2(expenseTotal + manualTotal + mileageTotal);
  }, [unbilled, manual, mileageLine]);

  const total = round2(subtotal + adjustments);

  if (!visit)
    return <div className="max-w-3xl mx-auto px-4 py-8 text-slate-500">Loading visit…</div>;

  if (visit.status !== 'Completed')
    return (
      <div className="max-w-3xl mx-auto px-4 py-8 space-y-3">
        <Link to={`/visits/${id}`} className="text-sm text-slate-500 hover:text-sky-600">
          ← Visit
        </Link>
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 text-sm text-amber-900">
          Close-out is only available once the visit is marked <strong>Completed</strong>. Current
          status: <strong>{visit.status}</strong>.
        </div>
      </div>
    );

  const onSend = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const res = await apiClient.post<ReceiptDetail>('/api/v1/receipts/final', {
        visitId: id,
        manualLineItems: manual,
        adjustments,
        includeMileage,
        notes: notes.trim() || null
      } satisfies CreateFinalPayload);
      nav(`/receipts/${res.data.id}`);
    } catch (err) {
      setError(readConflictMessage(err) ?? 'Create failed — try again.');
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-8 space-y-6">
      <div>
        <Link to={`/visits/${id}`} className="text-sm text-slate-500 hover:text-sky-600">← Visit</Link>
        <h1 className="text-2xl font-semibold text-slate-800 mt-2">Close-out — invoice</h1>
        <p className="text-slate-500 text-sm mt-1">{visit.customerName}</p>
      </div>

      <Stepper current={step} />

      {step === 'review' && (
        <section className="bg-white border border-slate-200 rounded-xl p-6 space-y-4">
          <h2 className="text-lg font-semibold text-slate-800">Expenses & mileage</h2>

          {unbilled.length === 0 ? (
            <p className="text-sm text-slate-500 italic">
              No unbilled expenses on this visit. You can still add manual lines in the next step.
            </p>
          ) : (
            <div>
              <p className="text-sm text-slate-600 mb-2">
                {unbilled.length} unbilled expense{unbilled.length === 1 ? '' : 's'} will be rolled
                into the invoice.
              </p>
              <ul className="text-sm space-y-1">
                {unbilled.map((e) => (
                  <li key={e.id} className="flex justify-between border-b border-slate-100 py-1">
                    <span>
                      <strong>{e.category}: </strong>{e.description}
                    </span>
                    <span className="font-mono">{e.amount.toFixed(2)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="border-t border-slate-100 pt-4">
            {visit.mileageMiles && visit.mileageMiles > 0 ? (
              <label className="flex items-start gap-3">
                <input
                  type="checkbox"
                  checked={includeMileage}
                  onChange={(e) => setIncludeMileage(e.target.checked)}
                  className="mt-1"
                />
                <span className="text-sm text-slate-700">
                  Include mileage: <strong>{visit.mileageMiles.toFixed(1)}</strong> miles ×{' '}
                  <strong>{perMileRate.toFixed(4)}</strong>/mi ={' '}
                  <strong>{(visit.mileageMiles * perMileRate).toFixed(2)}</strong>
                </span>
              </label>
            ) : (
              <p className="text-sm text-slate-500 italic">
                No mileage on this visit. Edit the visit to add mileage if needed.
              </p>
            )}
          </div>

          <div className="flex justify-end pt-2">
            <button
              type="button"
              onClick={() => setStep('extras')}
              className="bg-sky-600 text-white px-4 py-2 rounded hover:bg-sky-700 text-sm"
            >
              Next →
            </button>
          </div>
        </section>
      )}

      {step === 'extras' && (
        <section className="bg-white border border-slate-200 rounded-xl p-6 space-y-4">
          <h2 className="text-lg font-semibold text-slate-800">Manual lines + adjustments</h2>

          <div>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-slate-700">Extra line items</h3>
              <button
                type="button"
                onClick={() =>
                  setManual((m) => [...m, { description: '', quantity: 1, unitPrice: 0 }])
                }
                className="text-sky-600 hover:text-sky-700 text-sm inline-flex items-center gap-1"
              >
                <Plus size={14} /> Add line
              </button>
            </div>
            <div className="space-y-2">
              {manual.length === 0 && (
                <p className="text-sm text-slate-500 italic">No extra lines.</p>
              )}
              {manual.map((item, idx) => (
                <div key={idx} className="grid grid-cols-[1fr,80px,120px,40px] gap-2 items-center">
                  <input
                    type="text"
                    placeholder="Description"
                    value={item.description}
                    onChange={(e) =>
                      setManual((m) => m.map((l, i) => (i === idx ? { ...l, description: e.target.value } : l)))
                    }
                    className="border border-slate-300 rounded px-3 py-2 min-h-[44px]"
                  />
                  <input
                    type="number"
                    min={0}
                    step="0.01"
                    value={item.quantity}
                    onChange={(e) =>
                      setManual((m) => m.map((l, i) => (i === idx ? { ...l, quantity: Number(e.target.value) } : l)))
                    }
                    className="border border-slate-300 rounded px-3 py-2 min-h-[44px] text-right"
                  />
                  <MoneyInput
                    value={item.unitPrice}
                    onChange={(v) =>
                      setManual((m) => m.map((l, i) => (i === idx ? { ...l, unitPrice: v } : l)))
                    }
                    ariaLabel="Unit price"
                  />
                  <button
                    type="button"
                    onClick={() => setManual((m) => m.filter((_, i) => i !== idx))}
                    className="text-slate-400 hover:text-red-600"
                    aria-label="Remove line"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
            </div>
          </div>

          <label className="block">
            <span className="block text-sm font-medium text-slate-700 mb-1">
              Adjustment (+ or −)
            </span>
            <MoneyInput value={adjustments} onChange={setAdjustments} ariaLabel="Adjustment" />
            <span className="text-xs text-slate-500 mt-1 block">
              Add a negative value for a discount, positive for a tip or surcharge.
            </span>
          </label>

          <label className="block">
            <span className="block text-sm font-medium text-slate-700 mb-1">Notes</span>
            <textarea
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="w-full border border-slate-300 rounded px-3 py-2"
            />
          </label>

          <div className="flex justify-between pt-2">
            <button
              type="button"
              onClick={() => setStep('review')}
              className="text-slate-600 hover:text-sky-600 text-sm"
            >
              ← Back
            </button>
            <button
              type="button"
              onClick={() => setStep('confirm')}
              className="bg-sky-600 text-white px-4 py-2 rounded hover:bg-sky-700 text-sm"
            >
              Next →
            </button>
          </div>
        </section>
      )}

      {step === 'confirm' && (
        <section className="bg-white border border-slate-200 rounded-xl p-6 space-y-4">
          <h2 className="text-lg font-semibold text-slate-800">Preview</h2>

          <div className="border border-slate-200 rounded p-4 space-y-2 bg-slate-50">
            <p className="text-sm text-slate-700"><strong>Client: </strong>{visit.customerName}</p>

            {unbilled.length > 0 && (
              <>
                <hr className="border-slate-200" />
                <div>
                  <p className="text-xs uppercase tracking-wide text-slate-500">Expenses</p>
                  <ul className="text-sm">
                    {unbilled.map((e) => (
                      <li key={e.id} className="flex justify-between">
                        <span>{e.category}: {e.description}</span>
                        <span className="font-mono">{e.amount.toFixed(2)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </>
            )}

            {mileageLine && (
              <>
                <hr className="border-slate-200" />
                <div className="flex justify-between text-sm">
                  <span>Mileage ({mileageLine.miles.toFixed(1)} mi @ {mileageLine.rate.toFixed(4)}/mi)</span>
                  <span className="font-mono">{mileageLine.total.toFixed(2)}</span>
                </div>
              </>
            )}

            {manual.length > 0 && (
              <>
                <hr className="border-slate-200" />
                <div>
                  <p className="text-xs uppercase tracking-wide text-slate-500">Manual lines</p>
                  <ul className="text-sm">
                    {manual.map((m, i) => (
                      <li key={i} className="flex justify-between">
                        <span>{m.description}</span>
                        <span className="font-mono">{(m.quantity * m.unitPrice).toFixed(2)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </>
            )}

            <hr className="border-slate-200" />
            <div className="flex justify-between text-sm">
              <span>Subtotal</span>
              <span className="font-mono">{subtotal.toFixed(2)}</span>
            </div>
            {adjustments !== 0 && (
              <div className="flex justify-between text-sm">
                <span>Adjustment</span>
                <span className="font-mono">{adjustments.toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between text-base font-semibold text-slate-800 pt-1">
              <span>Total</span>
              <span className="font-mono">{total.toFixed(2)}</span>
            </div>
          </div>

          {error && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-2">
              {error}
            </p>
          )}

          <div className="flex justify-between pt-2">
            <button
              type="button"
              onClick={() => setStep('extras')}
              className="text-slate-600 hover:text-sky-600 text-sm"
            >
              ← Back
            </button>
            <button
              type="button"
              onClick={onSend}
              disabled={submitting || (unbilled.length === 0 && !mileageLine && manual.length === 0)}
              className="bg-sky-600 text-white px-4 py-2 rounded hover:bg-sky-700 disabled:opacity-50 text-sm"
            >
              {submitting ? 'Creating…' : 'Create invoice'}
            </button>
          </div>
        </section>
      )}
    </div>
  );
}

function Stepper({ current }: { current: Step }) {
  const steps: { id: Step; label: string }[] = [
    { id: 'review', label: '1. Review' },
    { id: 'extras', label: '2. Extras' },
    { id: 'confirm', label: '3. Confirm' }
  ];
  return (
    <ol className="flex gap-2 text-sm">
      {steps.map((s) => {
        const active = s.id === current;
        return (
          <li
            key={s.id}
            className={`px-3 py-1.5 rounded border ${
              active
                ? 'bg-sky-50 border-sky-300 text-sky-800 font-medium'
                : 'bg-white border-slate-200 text-slate-500'
            }`}
          >
            {s.label}
          </li>
        );
      })}
    </ol>
  );
}

function round2(value: number): number {
  return Math.round(value * 100) / 100;
}

function readConflictMessage(err: unknown): string | null {
  if (!axios.isAxiosError(err)) return null;
  const data = err.response?.data as { message?: string } | undefined;
  return data?.message ?? null;
}
